import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession, isSuperAdmin } from '../../adminSession';
import '../../Admin.scss';

// 성도 인원 한도 - 요금제 성도 규모 대비 성도 앱 사용 기기 수, 상위 요금제 자동 변경 예정 관리.
// 판정 규칙은 server/routers/common/churchUsage.js (2개월 연속 110% 초과 → 30일 이상 고지 후 다음 결제일 자동 적용).

type Level = 'normal' | 'warning' | 'over' | 'unlimited' | 'unknown';
type PendingStatus = 'scheduled' | 'objected' | 'held';

type HistoryRow = { month: string; activeDevices: number; rollMembers: number; memberLimit: number | null };

type Pending = {
  status: PendingStatus;
  memberSize: string | null;
  monthlyFee: number | null;
  effectiveDate: string | null;
  notifiedAt: string | null;
  note: string | null;
};

type UsageRow = {
  authChurch: string;
  churchName: string;
  planType: string;
  maintenanceStatus: string;
  monthlyFee: number | null;
  nextBillingDate: string | null;
  autoRenew: number;
  memberSize: string | null;
  memberLimit: number | null;
  planApps: string[] | null;
  computedFee: number | null;
  limitExempt: 0 | 1;
  limitExemptNote: string | null;
  activeDevices: number;
  rollMembers: number;
  level: Level;
  history: HistoryRow[];
  pending: Pending | null;
};

type Tier = { size: string; limit: number | null };
type Rules = { activeWindowDays: number; overRatio: number; overMonths: number; noticeDays: number; warningRatio: number };

type UsageResponse = { ok: boolean; data?: UsageRow[]; tiers?: Tier[]; apps?: string[]; rules?: Rules; message?: string };

type Filter = 'all' | 'attention' | 'over' | 'pending' | 'unknown';

const LEVEL_LABEL: Record<Level, string> = {
  normal: '정상',
  warning: '주의',
  over: '초과',
  unlimited: '한도 없음',
  unknown: '규모 미설정',
};
const LEVEL_TAG: Record<Level, string> = {
  normal: 'signed',
  warning: 'pending',
  over: 'danger',
  unlimited: 'signed',
  unknown: '',
};
const PENDING_LABEL: Record<PendingStatus, string> = {
  scheduled: '적용 예정',
  objected: '교회 이의 제기',
  held: '보류',
};
const PENDING_TAG: Record<PendingStatus, string> = { scheduled: 'pending', objected: 'danger', held: '' };

const FILTER_LABEL: Record<Filter, string> = {
  all: '전체',
  attention: '주의 이상',
  over: '초과',
  pending: '변경 예정',
  unknown: '규모 미설정',
};

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

function won(value: number | null | undefined) {
  return value != null ? `${value.toLocaleString()}원` : '-';
}

function shortMonth(month: string) {
  return `${Number(month.split('-')[1])}월`;
}

function matchesFilter(row: UsageRow, filter: Filter) {
  switch (filter) {
    case 'attention':
      return row.level === 'warning' || row.level === 'over';
    case 'over':
      return row.level === 'over';
    case 'pending':
      return row.pending != null;
    case 'unknown':
      return row.level === 'unknown';
    default:
      return true;
  }
}

function UsageBar({ row }: { row: UsageRow }) {
  if (row.memberLimit == null) {
    return <strong>{row.activeDevices.toLocaleString()}대</strong>;
  }
  const ratio = row.activeDevices / row.memberLimit;
  return (
    <div className="member-limit-usage">
      <div className="member-limit-usage-top">
        <strong>{row.activeDevices.toLocaleString()}대</strong>
        <span>{Math.round(ratio * 100)}%</span>
      </div>
      <div className="member-limit-bar">
        <div className={`member-limit-bar-fill member-limit-bar-fill--${row.level}`} style={{ width: `${Math.min(100, ratio * 100)}%` }} />
      </div>
    </div>
  );
}

type SettingsForm = {
  memberSize: string;
  apps: string[];
  recalcFee: boolean;
  limitExempt: boolean;
  limitExemptNote: string;
};

type PendingForm = { monthlyFee: string; effectiveDate: string; note: string };

export default function MemberLimitPage() {
  const session = getAdminSession();
  const canRunCheck = isSuperAdmin(session);
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [appNames, setAppNames] = useState<string[]>([]);
  const [rules, setRules] = useState<Rules | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [running, setRunning] = useState(false);

  const [settingsTarget, setSettingsTarget] = useState<UsageRow | null>(null);
  const [settingsForm, setSettingsForm] = useState<SettingsForm | null>(null);
  const [pendingTarget, setPendingTarget] = useState<UsageRow | null>(null);
  const [pendingForm, setPendingForm] = useState<PendingForm>({ monthlyFee: '', effectiveDate: '', note: '' });
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<UsageResponse>(`${MainURL}/admin-console/church-plans/usage`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok && res.data.data) {
        setRows(res.data.data);
        setTiers(res.data.tiers ?? []);
        setAppNames(res.data.apps ?? []);
        setRules(res.data.rules ?? null);
      } else {
        setErrorMsg(res.data?.message || '인원 한도 현황을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '인원 한도 현황을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const counts = useMemo(
    () =>
      (Object.keys(FILTER_LABEL) as Filter[]).reduce(
        (acc, key) => ({ ...acc, [key]: rows.filter((row) => matchesFilter(row, key)).length }),
        {} as Record<Filter, number>,
      ),
    [rows],
  );
  const visibleRows = rows.filter((row) => matchesFilter(row, filter));

  const handleRunCheck = async () => {
    if (!session) return;
    if (
      !window.confirm(
        '월간 점검을 지금 실행할까요?\n\n지난 30일 사용 기기 수를 지난달 기록으로 저장(이미 있으면 덮어씀)하고, 2개월 연속 초과한 교회에는 변경 예정을 잡아 교회에 메일로 알립니다.',
      )
    ) {
      return;
    }
    setRunning(true);
    try {
      const res = await axios.post(`${MainURL}/admin-console/church-plans/usage/run-check`, { requesterId: session.id });
      if (!res.data?.ok) throw new Error(res.data?.message);
      const summary = res.data.data?.summary ?? {};
      alert(`${res.data.data?.month} 점검 완료\n새 변경 예정 ${summary.scheduled ?? 0}건 · 요금 확인 필요 ${summary.held_no_apps ?? 0}건 · 자동 취소 ${summary.cancelled ?? 0}건`);
      fetchRows();
    } catch (err) {
      alert(errorMessageOf(err, '점검을 실행하지 못했습니다.'));
    } finally {
      setRunning(false);
    }
  };

  // ---------------- 설정 모달 ----------------
  const openSettings = (row: UsageRow) => {
    setSettingsTarget(row);
    setSettingsForm({
      memberSize: row.memberSize ?? '',
      apps: row.planApps ?? [],
      recalcFee: false,
      limitExempt: Boolean(row.limitExempt),
      limitExemptNote: row.limitExemptNote ?? '',
    });
  };
  const closeSettings = () => {
    setSettingsTarget(null);
    setSettingsForm(null);
  };

  const toggleApp = (name: string) => {
    setSettingsForm((prev) =>
      prev ? { ...prev, apps: prev.apps.includes(name) ? prev.apps.filter((a) => a !== name) : [...prev.apps, name] } : prev,
    );
  };

  const saveSettings = async () => {
    if (!session || !settingsTarget || !settingsForm) return;
    if (settingsForm.recalcFee && !settingsForm.memberSize) {
      alert('요금을 다시 계산하려면 성도 규모를 골라 주세요.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${MainURL}/admin-console/church-plans/${encodeURIComponent(settingsTarget.authChurch)}/limit`, {
        requesterId: session.id,
        memberSize: settingsForm.memberSize || null,
        planApps: settingsForm.apps,
        recalcFee: settingsForm.recalcFee,
        limitExempt: settingsForm.limitExempt,
        limitExemptNote: settingsForm.limitExemptNote,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      closeSettings();
      fetchRows();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  // ---------------- 변경 예정 모달 ----------------
  const openPending = (row: UsageRow) => {
    setPendingTarget(row);
    setPendingForm({
      monthlyFee: row.pending?.monthlyFee != null ? String(row.pending.monthlyFee) : '',
      effectiveDate: row.pending?.effectiveDate ?? '',
      note: row.pending?.note ?? '',
    });
  };
  const closePending = () => setPendingTarget(null);

  const submitPending = async (action: 'resume' | 'hold' | 'cancel') => {
    if (!session || !pendingTarget) return;
    const confirmText = {
      resume: '적용 예정으로 되돌립니다. 적용일이 되면 요금제가 자동으로 바뀝니다.',
      hold: '보류합니다. 다시 재개하기 전까지 적용되지 않습니다.',
      cancel: '변경 예정을 취소합니다. 다음 월간 점검에서 다시 초과로 잡히면 새로 예정됩니다.',
    }[action];
    if (!window.confirm(confirmText)) return;
    setSaving(true);
    try {
      const res = await axios.post(`${MainURL}/admin-console/church-plans/${encodeURIComponent(pendingTarget.authChurch)}/pending`, {
        requesterId: session.id,
        action,
        note: pendingForm.note,
        monthlyFee: action === 'resume' && pendingForm.monthlyFee ? Number(pendingForm.monthlyFee) : undefined,
        effectiveDate: action === 'resume' ? pendingForm.effectiveDate : undefined,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      closePending();
      fetchRows();
    } catch (err) {
      alert(errorMessageOf(err, '처리하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">성도 인원 한도</h2>
          <p className="admin-panel-desc">
            요금제 성도 규모와 최근 {rules?.activeWindowDays ?? 30}일 동안 성도 앱을 연 기기 수를 비교합니다.{' '}
            {rules
              ? `${rules.overMonths}개월 연속 한도의 ${Math.round(rules.overRatio * 100)}%를 넘으면 상위 규모로 변경 예정이 잡히고, 교회에 알린 뒤 ${rules.noticeDays}일 이상 지난 첫 결제일에 자동 적용됩니다(교회가 이의를 제기하면 멈춤).`
              : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canRunCheck && (
            <button type="button" className="admin-refresh-btn" onClick={handleRunCheck} disabled={running}>
              {running ? '점검 중...' : '월간 점검 지금 실행'}
            </button>
          )}
          <button type="button" className="admin-refresh-btn" onClick={fetchRows} disabled={loading}>
            {loading ? '불러오는 중...' : '새로고침'}
          </button>
        </div>
      </div>

      <div className="member-limit-filters">
        {(Object.keys(FILTER_LABEL) as Filter[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`member-limit-chip${filter === key ? ' is-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {FILTER_LABEL[key]} <span>{counts[key] ?? 0}</span>
          </button>
        ))}
      </div>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table member-limit-table">
          <thead>
            <tr>
              <th>교회</th>
              <th>성도 규모 · 월 요금</th>
              <th>최근 {rules?.activeWindowDays ?? 30}일 사용 기기</th>
              <th>교적 재적</th>
              <th>월별 기록</th>
              <th>변경 예정</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && !loading ? (
              <tr>
                <td colSpan={7}>해당하는 교회가 없습니다.</td>
              </tr>
            ) : (
              visibleRows.map((row) => (
                <tr key={row.authChurch}>
                  <td>
                    <div>{row.churchName}</div>
                    <div className="admin-subtext">{row.authChurch}</div>
                  </td>
                  <td>
                    <div>{row.memberSize ?? '미설정'}</div>
                    <div className="admin-subtext">
                      {won(row.monthlyFee)}
                      {row.computedFee != null && row.monthlyFee !== row.computedFee && ` (요금표 ${won(row.computedFee)})`}
                    </div>
                    {row.limitExempt ? (
                      <span className="admin-status-tag" title={row.limitExemptNote ?? ''}>
                        자동 변경 제외
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <UsageBar row={row} />
                    <span className={`admin-status-tag admin-status-tag--${LEVEL_TAG[row.level]}`}>{LEVEL_LABEL[row.level]}</span>
                  </td>
                  <td>{row.rollMembers.toLocaleString()}명</td>
                  <td>
                    {row.history.length === 0 ? (
                      <span className="admin-subtext">기록 없음</span>
                    ) : (
                      row.history.map((h) => (
                        <div key={h.month} className="admin-subtext">
                          {shortMonth(h.month)} {h.activeDevices.toLocaleString()}대
                          {h.memberLimit ? ` / ${h.memberLimit.toLocaleString()}` : ''}
                        </div>
                      ))
                    )}
                  </td>
                  <td>
                    {row.pending ? (
                      <>
                        <span className={`admin-status-tag admin-status-tag--${PENDING_TAG[row.pending.status]}`}>
                          {PENDING_LABEL[row.pending.status]}
                        </span>
                        <div className="admin-subtext">
                          → {row.pending.memberSize} · {won(row.pending.monthlyFee)}
                        </div>
                        <div className="admin-subtext">적용일 {row.pending.effectiveDate ?? '-'}</div>
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <button type="button" className="admin-btn edit-btn" onClick={() => openSettings(row)}>
                      설정
                    </button>
                    {row.pending && (
                      <button type="button" className="admin-btn edit-btn" onClick={() => openPending(row)}>
                        변경 예정
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {settingsTarget && settingsForm && (
        <div role="dialog" aria-modal="true" className="app-management-modal-overlay" onClick={closeSettings}>
          <div className="app-management-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{settingsTarget.churchName} 요금제 설정</h3>
            <p style={{ color: '#666', fontSize: 14 }}>
              성도 규모가 인원 한도를 정합니다. 선택 앱은 상위 요금제로 바뀔 때 새 월 요금을 계산하는 데 씁니다.
            </p>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>성도 규모</span>
              <select
                value={settingsForm.memberSize}
                onChange={(e) => setSettingsForm((prev) => (prev ? { ...prev, memberSize: e.target.value } : prev))}
              >
                <option value="">미설정 (한도 점검 안 함)</option>
                {tiers.map((tier) => (
                  <option key={tier.size} value={tier.size}>
                    {tier.size}
                    {tier.limit != null ? ` (한도 ${tier.limit.toLocaleString()})` : ' (한도 없음)'}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ marginBottom: 14 }}>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>선택 앱 (성도용 앱 + PC 관리자는 기본 포함)</span>
              {appNames.map((name) => (
                <label key={name} className="admin-form-checkbox" style={{ display: 'inline-flex', marginRight: 12, marginBottom: 0 }}>
                  <input type="checkbox" checked={settingsForm.apps.includes(name)} onChange={() => toggleApp(name)} />
                  {name}
                </label>
              ))}
            </div>
            <label className="admin-form-checkbox">
              <input
                type="checkbox"
                checked={settingsForm.recalcFee}
                onChange={(e) => setSettingsForm((prev) => (prev ? { ...prev, recalcFee: e.target.checked } : prev))}
              />
              월 요금도 요금표대로 다시 계산해 저장 (현재 {won(settingsTarget.monthlyFee)})
            </label>
            {settingsForm.recalcFee && !settingsForm.memberSize && (
              <p style={{ color: '#b91c1c', fontSize: 13 }}>성도 규모를 골라야 요금을 계산할 수 있습니다.</p>
            )}
            <label className="admin-form-checkbox">
              <input
                type="checkbox"
                checked={settingsForm.limitExempt}
                onChange={(e) => setSettingsForm((prev) => (prev ? { ...prev, limitExempt: e.target.checked } : prev))}
              />
              자동 변경 제외 (초과해도 변경 예정을 잡지 않음)
            </label>
            {settingsForm.limitExempt && (
              <label>
                <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>제외 사유</span>
                <input
                  type="text"
                  value={settingsForm.limitExemptNote}
                  onChange={(e) => setSettingsForm((prev) => (prev ? { ...prev, limitExemptNote: e.target.value } : prev))}
                  placeholder="예: 협력 교회, 행사 기간"
                />
              </label>
            )}
            {settingsTarget.pending && settingsForm.memberSize !== (settingsTarget.memberSize ?? '') && (
              <p style={{ color: '#b45309', fontSize: 13 }}>성도 규모를 바꾸면 지금 잡혀 있는 변경 예정은 취소됩니다.</p>
            )}
            <div className="am-actions">
              <button type="button" className="am-btn am-btn--secondary" onClick={closeSettings}>
                닫기
              </button>
              <button type="button" className="am-btn" onClick={saveSettings} disabled={saving}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingTarget && pendingTarget.pending && (
        <div role="dialog" aria-modal="true" className="app-management-modal-overlay" onClick={closePending}>
          <div className="app-management-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{pendingTarget.churchName} 변경 예정</h3>
            <p style={{ fontSize: 14 }}>
              <span className={`admin-status-tag admin-status-tag--${PENDING_TAG[pendingTarget.pending.status]}`}>
                {PENDING_LABEL[pendingTarget.pending.status]}
              </span>{' '}
              {pendingTarget.memberSize} → <strong>{pendingTarget.pending.memberSize}</strong>
            </p>
            <p style={{ color: '#666', fontSize: 13 }}>
              고지일 {pendingTarget.pending.notifiedAt ? String(pendingTarget.pending.notifiedAt).slice(0, 10) : '(교회에 아직 알리지 않음)'} · 현재 월
              요금 {won(pendingTarget.monthlyFee)} · 다음 결제일 {pendingTarget.nextBillingDate ?? '-'}
            </p>
            {pendingTarget.pending.note && (
              <p style={{ background: '#f8fafc', padding: 10, borderRadius: 6, fontSize: 13 }}>메모: {pendingTarget.pending.note}</p>
            )}
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>변경 후 월 요금 (원)</span>
              <input
                type="text"
                inputMode="numeric"
                value={pendingForm.monthlyFee}
                onChange={(e) => setPendingForm((prev) => ({ ...prev, monthlyFee: e.target.value.replace(/[^0-9]/g, '') }))}
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                적용일 — 교회에 알린 날로부터 {rules?.noticeDays ?? 30}일 이상 지난 결제일로 지정하세요
              </span>
              <input
                type="date"
                value={pendingForm.effectiveDate}
                onChange={(e) => setPendingForm((prev) => ({ ...prev, effectiveDate: e.target.value }))}
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>메모</span>
              <input
                type="text"
                value={pendingForm.note}
                onChange={(e) => setPendingForm((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="예: 교회와 통화 후 11월부터 적용하기로 함"
              />
            </label>
            <div className="am-actions">
              <button type="button" className="am-btn am-btn--secondary" onClick={closePending}>
                닫기
              </button>
              <button type="button" className="am-btn am-btn--secondary" onClick={() => submitPending('cancel')} disabled={saving}>
                변경 취소
              </button>
              {pendingTarget.pending.status !== 'held' && (
                <button type="button" className="am-btn am-btn--secondary" onClick={() => submitPending('hold')} disabled={saving}>
                  보류
                </button>
              )}
              <button type="button" className="am-btn" onClick={() => submitPending('resume')} disabled={saving}>
                {pendingTarget.pending.status === 'scheduled' ? '요금·적용일 저장' : '적용 예정으로 재개'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
